import {
  collectPage,
  type PositionedCalculatedAttributesRequest,
} from '@hyphengroup/cms-components/calculatedAttributes/collect';
import type { BlockVertical } from '@hyphengroup/cms-components/calculatedAttributes/moduleMode';
import type { BrandId } from '@hyphengroup/cms-components/utils/override';
import type { CmsBaseComponent } from '../cmsService/types';
import { planLoanScopes } from './plan';
import type { LoanScopePlan, PageWithLoanDefaults } from './types';
import type { LoanSearchParams } from '$/components/cms/helpers/loanSearchParams';

/** What the engine decided for one block, besides its figures. */
export type BlockCalculations = {
  /** The vertical the block runs under — its apply CTA's too (CMS-2052). */
  vertical: BlockVertical | undefined;
  /** The reader's loan filter moves its figures (CMS-1890). */
  followsPageFilter: boolean;
  /**
   * Its loan attributes are calculated whether or not figures exist: it runs
   * under Personal Loans by its own mode, not the page's (CMS-1928 R2).
   */
  ownsLoanAttributes: boolean;
};

/** The calculated-attributes engine's answer for one page. */
export type PageCalculations = {
  /** Every calculation the page's blocks ask for, for the host to execute. */
  requests: PositionedCalculatedAttributesRequest[];
  /** By block address; a block the engine does not know is absent. */
  blocks: ReadonlyMap<string, BlockCalculations>;
  /** The loan plan as the server sees it — without the reader's filter. */
  loans: LoanScopePlan;
  /** The loan plan with what the reader entered applied. */
  loansFor: (readerTerms: Partial<LoanSearchParams>) => LoanScopePlan;
};

/**
 * The engine for one page: collects every block once and answers everything
 * the server and the client ask of it. Neither reaches past it into the
 * collectors or the loan rules.
 */
export const createPageCalculations = ({
  brand,
  components,
  page,
}: {
  brand: BrandId;
  /** The page's components, flattened, in page order. */
  components: CmsBaseComponent[];
  page?: PageWithLoanDefaults;
}): PageCalculations => {
  const collected = collectPage(components, { vertical: page?.section });
  const requests = collected.flatMap((block) =>
    block.request ? [block.request] : [],
  );
  const loansFor = (readerTerms?: Partial<LoanSearchParams>) =>
    planLoanScopes({
      brand,
      components,
      requests,
      page,
      urlParams: readerTerms,
    });
  const loans = loansFor();

  // The filter's reach does not depend on the URL: a block's scope comes from
  // its own fields, and the reader's values only move the terms.
  const blocks = new Map(
    collected.map((block) => [
      block.moduleId,
      {
        vertical: block.vertical,
        followsPageFilter:
          block.request?.provider === 'PL_CALCULATOR' &&
          loans.modules[block.moduleId]?.scope === 'page',
        ownsLoanAttributes:
          block.statesVertical && block.vertical === 'PERSONAL_LOANS',
      },
    ]),
  );

  return { requests, blocks, loans, loansFor };
};
